import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueries } from "@tanstack/react-query";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { SegmentedFilter } from "@/components/ui/segmented-filter";
import { EmptyState } from "@/components/admin/payouts/EmptyState";
import {
  TherapistsStats,
  TherapistsTable,
  TherapistsListSkeleton,
} from "@/components/admin/therapists";
import { parseSpecializations } from "@/components/admin/therapists/helpers";
import { MagnifyingGlass, ArrowCounterClockwise } from "@phosphor-icons/react";
import { THERAPIST_STATUS_CONFIG } from "@/lib/status-configs";
import type { Therapist } from "@/types";

export const Route = createFileRoute("/dashboard/admin/therapists/")({
  component: AdminTherapists,
});

type StatusFilter = "pending" | "approved" | "rejected";

const TAB_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "pending", label: "Na čekanju" },
  { value: "approved", label: "Odobreni" },
  { value: "rejected", label: "Odbijeni" },
];

const STATUSES: StatusFilter[] = ["pending", "approved", "rejected"];

async function fetchTherapists(status: StatusFilter): Promise<Therapist[]> {
  const res = await fetch(`/api/therapists?status=${status}&includeDeactivated=1`);
  if (!res.ok) throw new Error("Failed to fetch therapists");
  return res.json();
}

function AdminTherapists() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
  const [searchQuery, setSearchQuery] = useState("");

  // One query per status: the active tab renders from it, the other two feed
  // the stat cards and pre-warm tab switches.
  const queries = useQueries({
    queries: STATUSES.map((status) => ({
      queryKey: ["admin", "therapists", status],
      queryFn: () => fetchTherapists(status),
    })),
  });
  const byStatus = Object.fromEntries(
    STATUSES.map((status, i) => [status, queries[i]]),
  ) as Record<StatusFilter, (typeof queries)[number]>;

  const approved = byStatus.approved.data;
  const counts = {
    pending: byStatus.pending.data?.length,
    active: approved?.filter((t) => !t.deactivatedAt).length,
    deactivated: approved?.filter((t) => t.deactivatedAt).length,
    rejected: byStatus.rejected.data?.length,
  };

  const activeQuery = byStatus[statusFilter];
  const therapists = activeQuery.data ?? [];

  const query = searchQuery.trim().toLowerCase();
  const filtered = query
    ? therapists.filter(
        (t) =>
          t.userName?.toLowerCase().includes(query) ||
          t.userEmail?.toLowerCase().includes(query) ||
          parseSpecializations(t.specializations).some((s) =>
            s.toLowerCase().includes(query),
          ),
      )
    : therapists;

  const statusConfig = THERAPIST_STATUS_CONFIG[statusFilter];

  return (
    <div className="mx-auto max-w-6xl space-y-5 pb-14 sm:space-y-6">
      {/* Header */}
      <header className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1
            className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Terapeuti
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Prijave, odobreni profili i deaktivacije terapeuta.
          </p>
        </div>

        <SegmentedFilter
          ariaLabel="Status terapeuta"
          options={TAB_OPTIONS}
          value={statusFilter}
          onChange={setStatusFilter}
        />
      </header>

      <TherapistsStats counts={counts} />

      {/* Search */}
      <div className="flex items-center gap-1 overflow-hidden rounded-2xl border border-border/60 bg-card px-4 shadow-sm">
        <MagnifyingGlass className="size-4 shrink-0 text-muted-foreground" />
        <Input
          placeholder="Pretraži po imenu, emailu ili specijalizaciji..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="h-12 border-0 bg-transparent shadow-none focus-visible:ring-0"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ArrowCounterClockwise className="size-3" />
            Očisti
          </button>
        )}
      </div>

      {/* Content */}
      {activeQuery.isLoading ? (
        <TherapistsListSkeleton />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={statusConfig.icon}
          title={
            searchQuery
              ? "Nema rezultata pretrage"
              : `Nema terapeuta — ${statusConfig.label.toLowerCase()}`
          }
          description={
            searchQuery
              ? "Pokušajte s drugačijim pojmom za pretragu."
              : statusFilter === "pending"
                ? "Trenutno nema novih prijava koje čekaju odobrenje."
                : statusFilter === "approved"
                  ? "Još niste odobrili nijednog terapeuta."
                  : "Nema odbijenih prijava terapeuta."
          }
          hasFilters={searchQuery.length > 0}
          onClear={() => setSearchQuery("")}
        />
      ) : (
        <TherapistsTable therapists={filtered} />
      )}
    </div>
  );
}
